import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Heading, Text } from '@/components/ui/typography';
import { Input } from '@/components/ui/input';
import { Loader2, Upload, CheckCircle, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const UploadPopup = ({ jobId, onClose, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    stage: 'preparing',
    message: 'Preparing to upload...'
  });

  // New state to hold duplicates returned from backend
  const [duplicates, setDuplicates] = useState([]);

  const progressMessages = {
    preparing: 'Preparing files for upload...',
    uploading: 'Uploading resumes to server...',
    scoring: 'Calculating match scores...',
    finalizing: 'Finalizing results...'
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFile(files);
    setError('');
    // reset any previous duplicate info when selecting new files
    setDuplicates([]);
  };

  const updateProgress = (stage) => {
    setUploadProgress({
      stage,
      message: progressMessages[stage]
    });
  };

  const handleUpload = async () => {
    if (selectedFile.length === 0) {
      setError('Please select at least one PDF file');
      return;
    }

    setIsUploading(true);
    updateProgress('preparing');

    try {
      const formData = new FormData();
      selectedFile.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });
      formData.append('job_id', jobId);

      updateProgress('uploading');

      const response = await fetch('/api/handle_resume_upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => 'File upload failed.');
        throw new Error(errText || 'File upload failed.');
      }

      updateProgress('scoring');
      // small UX delay to show progress stages
      await new Promise(resolve => setTimeout(resolve, 500));

      updateProgress('finalizing');
      await new Promise(resolve => setTimeout(resolve, 500));

      const result = await response.json();

      // set duplicates to show in UI
      const dupList = result.duplicates || [];
      setDuplicates(dupList)

      console.log(duplicates)

      // call parent callback with successful new candidates
      onUploadSuccess(result.newCandidates || []);

      setShowSuccess(true);

      // if there are no duplicates we auto-close after 3s, otherwise keep open so user can inspect
      if (!dupList.length) {
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (err) {
      setError('An error occurred while uploading the file');
      console.error('Error uploading files:', err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      {!showSuccess && (
        <Dialog open={true} onOpenChange={onClose}>
          <DialogContent className={cn("sm:max-w-[500px]")}>
            <DialogHeader>
              <DialogTitle><Heading level="h2">Upload Resumes</Heading></DialogTitle>
              <DialogDescription>
                <Text>Please select files to upload.</Text>
              </DialogDescription>
            </DialogHeader>

            {isUploading ? (
              <div className={cn("flex flex-col items-center justify-center py-8")}>
                <Loader2 className={cn("w-8 h-8 animate-spin mb-4")} />
                <Text className={cn("font-semibold mb-4")}>{uploadProgress.message}</Text>
                <div className={cn("w-full space-y-2")}>
                  {Object.keys(progressMessages).map((stage) => (
                    <div 
                      key={stage}
                      className={cn("p-2 rounded-md", uploadProgress.stage === stage ? 'bg-blue-100' : 'bg-gray-100')}
                    >
                      <Text className={cn("text-sm")}>{progressMessages[stage]}</Text>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <Input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.txt"
                  multiple
                  className={cn("w-full")}
                />
                {error && <Text className={cn("text-red-600 text-sm mt-2")}>{error}</Text>}
                {selectedFile.length > 0 && (
                  <div className={cn("mt-4 p-4 bg-gray-50 rounded-md")}>
                    <Heading level="h3" className={cn("text-lg font-semibold mb-2")}>Selected Files:</Heading>
                    <ul className={cn("list-disc list-inside space-y-1")}>
                      {selectedFile.map((file, index) => (
                        <li key={index}><Text className={cn("text-sm")}>{file.name}</Text></li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* If previous upload returned duplicates, show a preview here before next upload */}
                {duplicates.length > 0 && (
                  <div className={cn("mt-4 p-4 bg-yellow-50 rounded-md border border-yellow-200")}>
                    <Heading level="h4" className={cn("text-md font-semibold mb-2")}>Previously detected duplicates (from last upload):</Heading>
                    <ul className={cn("list-disc list-inside space-y-1")}>
                      {duplicates.map((d, i) => (
                        <li key={i}>
                          <Text className={cn("text-sm")}>
                            <strong>{d.uploaded_filename || 'unknown'}</strong>
                            {d.reasons && d.reasons.length > 0 && (
                              <span> — {d.reasons.join(', ')}</span>
                            )}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={onClose}
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpload}
                disabled={isUploading || selectedFile.length === 0}
              >
                {isUploading ? (
                  <>
                    <Loader2 className={cn("w-4 h-4 mr-2 animate-spin")} />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className={cn("w-4 h-4 mr-2")} />
                    Upload CV
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {showSuccess && (
        <Dialog open={showSuccess} onOpenChange={onClose}>
          <DialogContent className={cn("sm:max-w-[500px]")}>
            <DialogHeader>
              <DialogTitle className={cn("flex flex-col items-center")}>
                <CheckCircle className={cn("w-16 h-16 text-green-500 mb-4")} />
                <Heading level="h2">Success!</Heading>
              </DialogTitle>
              <DialogDescription>
                <Text>Resumes processed successfully</Text>
              </DialogDescription>
            </DialogHeader>
            <div className={cn("text-center space-y-2 mb-4")}>
              <Text>{selectedFile.length} resume(s) analyzed</Text>
              <Text>AI matching scores calculated</Text>
            </div>

            {/* SHOW DUPLICATES DETAILS IN SUCCESS MODAL */}
            {duplicates.length > 0 ? (
              <div className={cn("mt-4 p-4 bg-yellow-50 rounded-md border border-yellow-200")}>
                <Heading level="h3" className={cn("text-lg font-semibold mb-2")}>Duplicates skipped</Heading>
                <Text className={cn("text-sm mb-4")}>The following files were detected as duplicates and were not uploaded:</Text>
                <ul className={cn("space-y-3 mb-4")}>
                  {duplicates.map((d, i) => (
                    <li key={i} className={cn("p-3 bg-white rounded-md")}>
                      <Text className={cn("font-semibold block mb-2")}>{d.uploaded_filename || 'unknown'}</Text>
                      <div className={cn("space-y-1 text-sm")}>
                        {d.candidate_name && <Text>Name: {d.candidate_name}</Text>}
                        {d.candidate_email && <Text>Email: {d.candidate_email}</Text>}
                        {d.candidate_phone && <Text>Phone: {d.candidate_phone}</Text>}
                        {d.reasons && d.reasons.length > 0 && (
                          <Text>Reason: {d.reasons.join(', ')}</Text>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                <Text className={cn("text-xs text-gray-600")}>If you believe a file was incorrectly flagged, please review the resume or contact support.</Text>
              </div>
            ) : null}

            <DialogFooter>
              <Button onClick={onClose}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default UploadPopup;
